import { useState, useEffect, createContext, useContext } from 'react';
import { auth, authApi } from '../services/api';

export interface User {
  id: string;
  username: string;
  email: string;
  full_name: string;
  role: string;
  tenant_id: string | null;
  permissions: string[];
}

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  checkAuthStatus: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useAuthProvider = (): AuthContextType => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuthStatus = () => {
    const token = auth.getToken();
    if (token && auth.isAuthenticated()) {
      // Decode token to get user info
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUser({
          id: payload.user_id || payload.sub,
          username: payload.username || payload.sub,
          email: payload.email || '',
          full_name: payload.full_name || payload.username,
          role: payload.role || 'user',
          tenant_id: payload.tenant_id || null,
          permissions: payload.permissions || []
        });
      } catch (error) {
        console.error('Error decoding token:', error);
        auth.removeToken();
      }
    }
    setIsLoading(false);
  };

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      const response = await authApi.login({ username, password });
      const { access_token, user_info } = response.data;

      auth.setToken(access_token);

      if (user_info) {
        setUser(user_info);
      } else {
        // Fallback: decode token for user info
        checkAuthStatus();
      }

      return true;
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  };

  const logout = () => {
    auth.removeToken();
    setUser(null);
  };

  useEffect(() => {
    checkAuthStatus();
  }, []);

  return {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    checkAuthStatus
  };
};

export { AuthContext };